import PropTypes from 'prop-types';
/* eslint-disable camelcase -- React Router's internal context exports use UNSAFE_ naming convention */
import {
  UNSAFE_LocationContext,
  UNSAFE_NavigationContext,
  UNSAFE_RouteContext,
  MemoryRouter,
  Routes,
  Route,
  useParams,
} from 'react-router-dom';
/* eslint-enable camelcase */

import { BusinessEntityGate, BusinessEntityProvider } from '@zenbusiness/application-commons-business-entity';
import { useEnv } from '@zenbusiness/application-commons-env';
import { ExperimentsIdentity } from '@zenbusiness/application-commons-experiments';

import ClickstreamWrapper from '#client/App/ClickstreamWrapper';
import PriceListWrapper from '#client/App/PriceListWrapper';
import { DEFAULT_SEGMENT } from '#client/build-env';
import { SlideCheckout } from '#client/components/GlobalComponents/SlideCheckout';
import { OrderTrackerStatusProvider } from '#client/hooks/useOrderTrackerStatus';
import ProductLandingWrapper from '#client/pages/ProductLanding';

import FederatedEntry from '../FederatedEntry';

/**
 * Stable context values for the router context provider resets.
 * Extracted as module-level constants to avoid creating new objects on every
 * render, which would trigger unnecessary re-renders in consuming components.
 */
const RESET_NAVIGATION_CONTEXT = { basename: '', navigator: null, static: false };
const RESET_ROUTE_CONTEXT = { outlet: null, matches: [], isDataRoute: false };

/**
 * Resets the React Router context so that a new MemoryRouter can be rendered
 * inside a host application that already has its own Router.
 *
 * WHY THIS IS NEEDED:
 * This federated remote entry is designed to be mounted by any MFE in the
 * service cluster (e.g. assistant-ui, compliance-ui, or any future host).
 * Those host applications each have their own top-level BrowserRouter.
 * React Router v6 does not allow nested Routers -- rendering a MemoryRouter
 * inside the host's BrowserRouter throws:
 *   "You cannot render a <Router> inside another <Router>."
 *
 * By nullifying the parent Router's context providers, we create an isolated
 * routing boundary. The MemoryRouter below operates independently, which means
 * useParams(), useNavigate(), useSearchParams(), and all other router hooks
 * inside the product landing pages resolve against the MemoryRouter -- not the
 * host app's router. This is a well-established pattern for micro-frontend
 * routing isolation.
 *
 * THREE CONTEXTS MUST BE RESET:
 * - NavigationContext: prevents the "nested Router" error by clearing the
 *   parent's navigator reference.
 * - LocationContext: ensures MemoryRouter provides its own location rather
 *   than inheriting the host's browser URL.
 * - RouteContext: clears the host's matched route segments so that Routes
 *   inside the MemoryRouter matches against the full pathname instead of
 *   attempting to resolve relative to the host's already-matched routes.
 *
 * NOTE ON THE UNSAFE_ PREFIX:
 * The "UNSAFE_" prefix is a React Router API stability warning (similar to
 * React's own UNSAFE_ lifecycle methods), NOT a security or correctness
 * concern. These context symbols are the documented way to access the internal
 * router contexts and are stable within the v6 major version. If React Router
 * v7 changes them, only this wrapper needs updating.
 *
 * @param {object} props - Component props.
 * @param {import('react').ReactNode} props.children - Child elements to render
 *   inside the isolated routing boundary.
 * @returns {import('react').ReactElement} Children wrapped with reset router contexts.
 */
function RouterIsolation({ children }) {
  return (
    // eslint-disable-next-line camelcase, react/jsx-pascal-case -- see JSDoc above for full rationale
    <UNSAFE_NavigationContext.Provider value={RESET_NAVIGATION_CONTEXT}>
      {/* eslint-disable-next-line camelcase, react/jsx-pascal-case -- see JSDoc above for full rationale */}
      <UNSAFE_LocationContext.Provider value={null}>
        {/* eslint-disable-next-line camelcase, react/jsx-pascal-case -- see JSDoc above for full rationale */}
        <UNSAFE_RouteContext.Provider value={RESET_ROUTE_CONTEXT}>{children}</UNSAFE_RouteContext.Provider>
      </UNSAFE_LocationContext.Provider>
    </UNSAFE_NavigationContext.Provider>
  );
}

RouterIsolation.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * Provides the contexts that ProductLanding components require. In the normal
 * dashboard-ui app, these are set up by the Authenticated layout route. In the
 * federated context we set them up directly since we bypass that layout.
 *
 * @param {object} props - Component props.
 * @param {import('react').ReactNode} props.children - Child elements to render.
 * @returns {import('react').ReactElement} Children wrapped with required providers.
 */
function ProductLandingProviders({ children }) {
  const { businessEntityUuid } = useParams();
  // Note: Future requirements may ask for the option to receive a price list uuid as a prop from the host app
  // For now, we use the default price list uuid from the environment variables
  // In the future, we may need to add a prop to the ProductLandingProviders component to receive the price list uuid from the host app
  // and use that instead of the default price list uuid from the environment variables with a fallback to the default price list uuid
  const { DEFAULT_PRICE_LIST_UUID } = useEnv('root-first');

  return (
    <BusinessEntityProvider businessEntityUuid={businessEntityUuid}>
      <BusinessEntityGate loadingElement={null}>
        <OrderTrackerStatusProvider>
          <PriceListWrapper priceListUuid={DEFAULT_PRICE_LIST_UUID}>
            <ClickstreamWrapper>
              <ExperimentsIdentity />
              {children}
            </ClickstreamWrapper>
          </PriceListWrapper>
        </OrderTrackerStatusProvider>
      </BusinessEntityGate>
    </BusinessEntityProvider>
  );
}

ProductLandingProviders.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * Federated remote entry for dashboard-ui Product Landing pages.
 *
 * Renders a full product landing page within an isolated MemoryRouter so that
 * it can be mounted inside any host MFE without conflicting with the host's
 * own router. The `path` prop should be a dashboard-relative URL path
 * (e.g. "/dashboard/<uuid>/products/quarterly-tax-planning") which seeds the
 * MemoryRouter's initial location for useParams() compatibility.
 *
 * @param {object} props - Component props.
 * @param {string} props.path - The full dashboard-relative URL path for the
 *   product page (e.g. "/dashboard/:uuid/products/:productId").
 * @returns {import('react').ReactElement} The product landing page wrapped in
 *   federated providers and an isolated router.
 */
export default function ProductLandingEntry({ path }) {
  return (
    <FederatedEntry>
      <RouterIsolation>
        <MemoryRouter initialEntries={[path]}>
          <Routes>
            <Route
              path={`${DEFAULT_SEGMENT}/:businessEntityUuid/products/:productId`}
              element={
                <ProductLandingProviders>
                  <ProductLandingWrapper federated />
                  <SlideCheckout />
                </ProductLandingProviders>
              }
            />
          </Routes>
        </MemoryRouter>
      </RouterIsolation>
    </FederatedEntry>
  );
}

ProductLandingEntry.propTypes = {
  path: PropTypes.string.isRequired,
};
